<template>
  <div>
    <div class="py-3"></div>
    <v-card class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>
      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>전화번호 인증</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filter.app_pid"
              :items="appItems"
              item-text="text"
              item-value="value"
              label="앱"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filter.status"
              :items="statusItems"
              item-text="text"
              item-value="value"
              label="상태"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filter.user_key"
              label="유저키"
              outlined
              dense
              hide-details
              clearable
              @keyup.enter="loadLogs"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filter.phone_number"
              label="전화번호"
              outlined
              dense
              hide-details
              clearable
              @keyup.enter="loadLogs"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn color="primary" block :disabled="loading" @click="loadLogs">
              <v-icon left>mdi-magnify</v-icon>
              조회
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn color="grey darken-1" dark block :disabled="loading" @click="refresh">
              <v-icon left>mdi-refresh</v-icon>
              새로고침
            </v-btn>
          </v-col>
        </v-row>

        <v-row dense align="center" class="mt-1">
          <v-col cols="12">
            <v-checkbox
              v-model="onlyNeedsResend"
              label="포스트백 미수신만 보기"
              hide-details
              dense
              class="mt-0"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-text class="pa-0">
        <v-data-table
          v-if="filteredLogs.length > 0"
          :headers="headers"
          :items="filteredLogs"
          :items-per-page="50"
          class="elevation-0"
          :footer-props="{ 'items-per-page-options': [50, 100, 300, -1] }"
        >
          <template v-slot:item.appName="{ item }">
            {{ item.appName || appName(item.appPid) }}
          </template>

          <template v-slot:item.userKey="{ item }">
            <span class="text-body-2">{{ item.userKey || '-' }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <div>
              <v-chip small dark :color="statusColor(item.status)">
                {{ item.status || '-' }}
              </v-chip>
              <div class="text-caption grey--text mt-1">
                {{ item.authCode || '-' }}
              </div>
            </div>
          </template>

          <template v-slot:item.phoneNumber="{ item }">
            {{ item.phoneNumber || '-' }}
          </template>

          <template v-slot:item.network="{ item }">
            {{ item.network || '-' }}
          </template>

          <template v-slot:item.createdAt="{ item }">
            <div>
              {{ fmtTime(item.createdAt) }}
              <div
                v-if="item.verifiedAt"
                class="text-caption green--text mt-1"
                :title="`인증완료: ${fmtTime(item.verifiedAt)}`"
              >
                인증완료 {{ fmtTime(item.verifiedAt) }}
              </div>
            </div>
          </template>

          <template v-slot:item.postbackStatus="{ item }">
            <div>
              <v-chip
                small
                dark
                :color="postbackColor(postbackOf(item).status)"
                :title="postbackTitle(postbackOf(item))"
              >
                {{ postbackOf(item).status || '미적재' }}
              </v-chip>

              <div
                v-if="!postbackOf(item).status"
                class="text-caption grey--text mt-1"
                title="큐 미적재(앱에 포스트백 URL 미설정)"
              >
                큐 미적재(앱에 포스트백 URL 미설정)
              </div>

              <div
                v-else-if="postbackOf(item).lastError"
                class="text-caption grey--text mt-1"
                :title="postbackOf(item).lastError"
              >
                {{ postbackOf(item).lastError }}
              </div>

              <div
                v-if="postbackOf(item).retryCount !== null && postbackOf(item).retryCount !== undefined"
                class="text-caption grey--text"
              >
                재시도 {{ postbackOf(item).retryCount }}/{{ postbackOf(item).maxRetry }}
              </div>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="postbackOf(item).needsResend"
              small
              color="orange darken-2"
              dark
              :loading="!!busy[item.id]"
              :disabled="!!busy[item.id]"
              @click="resend(item)"
            >
              <v-icon left small>mdi-send</v-icon>
              재전송
            </v-btn>
            <span v-else class="text-caption grey--text">-</span>
          </template>
        </v-data-table>

        <v-container v-else class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-cellphone-check</v-icon>
          <p class="text-body-1 grey--text mt-3">조회된 인증 내역이 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { phoneAuth, housead } from '../../api';

export default {
  name: 'PhoneAuth_Logs',
  data() {
    return {
      loading: false,
      apps: [],
      logs: [],
      busy: {},
      onlyNeedsResend: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      filter: {
        limit: 300,
        app_pid: null,
        status: '',
        user_key: '',
        phone_number: '',
      },
      statusItems: [
        { text: '전체', value: '' },
        { text: 'PENDING', value: 'PENDING' },
        { text: 'VERIFIED', value: 'VERIFIED' },
        { text: 'DUPLICATE(중복)', value: 'DUPLICATE' },
        { text: 'EXPIRED', value: 'EXPIRED' },
      ],
      headers: [
        { text: '앱', value: 'appName', width: '140' },
        { text: '유저키', value: 'userKey', width: '180' },
        { text: '상태', value: 'status', width: '150' },
        { text: '전화번호', value: 'phoneNumber', width: '140' },
        { text: '통신사', value: 'network', width: '100' },
        { text: '인증시도 시간', value: 'createdAt', width: '170' },
        { text: '포스트백', value: 'postbackStatus', width: '240', sortable: false },
        { text: '액션', value: 'actions', width: '130', sortable: false },
      ],
    };
  },
  computed: {
    appItems() {
      const list = (this.apps || []).map((a) => ({ text: a.appName || `앱 #${a.id}`, value: a.id }));
      return [{ text: '전체', value: null }].concat(list);
    },
    // 포스트백 미수신 필터는 서버 파라미터가 아니라 클라이언트 필터
    filteredLogs() {
      if (!this.onlyNeedsResend) return this.logs;
      return (this.logs || []).filter((row) => this.postbackOf(row).needsResend === true);
    },
  },
  mounted() {
    this.loadApps();
    this.loadLogs();
  },
  methods: {
    // 값이 있는 키만 담는다 (빈 문자열이 쿼리에 들어가면 백엔드 필터가 오작동)
    buildParams(source) {
      const params = {};
      Object.keys(source).forEach((key) => {
        const value = source[key];
        if (value === null || value === undefined) return;
        if (typeof value === 'string' && value.trim() === '') return;
        params[key] = typeof value === 'string' ? value.trim() : value;
      });
      return params;
    },
    // postback이 null/undefined여도 안전하게 접근
    postbackOf(row) {
      return (row && row.postback) || {};
    },
    // DB datetime(ISO/문자열)을 로컬 시간 'YYYY-MM-DD HH:mm:ss' 로 표기
    fmtTime(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    appName(appPid) {
      if (appPid === null || appPid === undefined || appPid === '') return '-';
      const found = (this.apps || []).find((a) => String(a.id) === String(appPid));
      return (found && found.appName) ? found.appName : appPid;
    },
    statusColor(status) {
      switch (status) {
        case 'PENDING': return 'grey';
        case 'VERIFIED': return 'green';
        case 'DUPLICATE': return 'red';
        case 'EXPIRED': return 'orange';
        default: return 'grey';
      }
    },
    postbackColor(status) {
      switch (status) {
        case 'SUCCESS': return 'green';
        case 'READY':
        case 'SENDING': return 'blue';
        case 'FAILED': return 'orange';
        case 'DEAD': return 'red';
        default: return 'grey';
      }
    },
    postbackTitle(pb) {
      if (!pb || !pb.status) return '큐 미적재(앱에 포스트백 URL 미설정)';
      if (pb.lastError) return `실패 사유: ${pb.lastError}`;
      return pb.sentAt ? `전송시각: ${pb.sentAt}` : pb.status;
    },
    notify(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || 'success';
      this.snackbar = true;
    },
    // 앱 목록 실패는 치명적이지 않으므로 조용히 무시
    loadApps() {
      return housead.getApps()
        // getApps 는 { list:[...] } 형태로 온다 (배열 아님) → list 우선 언랩
        .then((res) => {
          this.apps = Array.isArray(res) ? res : ((res && Array.isArray(res.list)) ? res.list : []);
        })
        .catch((e) => { console.error('앱 목록 조회 실패:', e); });
    },
    loadLogs() {
      this.loading = true;
      phoneAuth.getLogs(this.buildParams(this.filter))
        .then((res) => { this.logs = Array.isArray(res) ? res : []; })
        .catch((e) => { alert(typeof e === 'string' ? e : '조회에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    refresh() {
      this.loadApps();
      this.loadLogs();
    },
    // 재전송 여부는 서버가 내려준 needsResend만 신뢰한다
    resend(row) {
      if (!row || !row.id) return;
      if (!window.confirm('해당 건의 포스트백을 재전송하시겠습니까?')) return;

      this.$set(this.busy, row.id, true);
      phoneAuth.resendPostback(row.id)
        .then(() => {
          this.notify('포스트백 재전송을 요청했습니다.', 'success');
          return this.loadLogs();
        })
        .catch((e) => {
          this.notify(typeof e === 'string' ? e : '재전송에 실패했습니다.', 'error');
        })
        .finally(() => { this.$set(this.busy, row.id, false); });
    },
  },
};
</script>
